import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {useSelector} from 'react-redux';

import {labels} from '../../../util/utils';
import normalizeData from './utils/normalizeData';

import columns from './columns';

import {IconoirSearch} from '@ey-xd/motif-icon';
import OutputText from '@ey/canvascoreservices/OutputText';

import CustomTable from '../../CoreWrappers/CustomTable';
import Accordion from '../../CoreWrappers/Accordion';
import Search from '../../CoreWrappers/Search';
import Badge from '../../CoreWrappers/Badge';
import ListWithScroll from '../ListWithScroll';
import ColumnVisibilityDropdown, {columnVisibilityOptionsPropTypes} from './ColumnVisibilityDropdown';
import StyledMetricsTableHeader from './StyledMetricsTableHeader';
import StyledMetricsTable from './StyledMetricsTable';
import StyledGroupLabel from './StyledGroupLabel';
import filterDataByString from './utils/filterDataByString';

const variants = [
	'milestones',
	'keyevents',
	'primary',
	'component',
	'ccp',
	'assignments',
	'notes',
	'risks',
	'strategy',
	'findings'
]; // TODO: Rename this constant with an appropiate name.

const MetricsTable = ({
	data = null,
	renderHeader = null,
	headerConfig = {
		$columns: 'var(--px-270) 1fr 1fr 1fr max-content',
		$areas: 'search . customizeColumns tableSelect exportData'
	},
	renderFilter = null,
	variant = variants[0],
	columnVisibility = null,
	columnVisibilityOptions = null,
	maxCustomColumns = 4
}) => {
	const [visibilityValue, setVisibilityValue] = useState([]);
	const [{searchString, filterString}, setSearch] = useState({searchString: '', filterString: null});
	const [openAccordions, setOpenAccordions] = useState([]);

	const userPreferences = useSelector(({userPreferences}) => userPreferences);

	useEffect(() => {
		if (columnVisibility !== null) setVisibilityValue(columnVisibility);
	}, [columnVisibility]);

	if (!data) return null;

	const displayData = normalizeData(filterDataByString(data, filterString));

	const tableHeaderLabels = {
		engagementLabel: labels.engagementLabel,
		start: labels.start,
		end: labels.end,
		status: labels.status,
		reportReleaseDate: labels.reportReleaseDate,
		partnerInvited: labels.partnerInvited,
		picSignoffOnCra: labels.picSignoffOnCra,
		picSignoffOnAuditPlan: labels.picSignoffOnAuditPlan,
		eqrSignoffOnCra: labels.eqrSignoffOnCra,
		groupAuditTotalForms: labels.groupAuditTotalForms,
		overdueLabel: labels.overdueLabel,
		returned: labels.returned,
		totalTasksCapitalized: labels.totalTasksCapitalized,
		groupAuditNotSent: labels.groupAuditNotSent,
		groupAuditUnlinked: labels.groupAuditUnlinked,
		requests: labels.requests,
		comingDue: labels.comingDue,
		preparerAssignments: labels.preparerAssignments,
		preparerNotAssigned: labels.preparerNotAssigned,
		reviewerAssignments: labels.reviewerAssignments,
		reviewerNotAssigned: labels.reviewerNotAssigned,
		groupRole: labels.groupRole,
		totalRisks: labels.totalRisks,
		byType: labels.byType,
		digitalAuditLabel: labels.digitalAuditLabel,
		controls: labels.controls,
		substantiveProcedures: labels.substantiveProcedures,
		MisstatementsLabel: labels.MisstatementsLabel,
		DeficienciesLabel: labels.DeficienciesLabel,
		reportType: labels.reportType,
		archiveDeadline: labels.archiveDeadline,
		reportDate: labels.reportDate,
		geography: labels.geography,
		standars: labels.standars,
		gaap: labels.gaap,
		taskStatus: labels.taskStatus,
		totalNotesLabel: labels.totalNotesLabel,
		openNotes: labels.openNotes,
		byPriority: labels.byPriority,
		low: labels.low,
		medium: labels.medium,
		high: labels.high,
		critical: labels.critical,
		itlabel: labels.itlabel,
		yearEndDate: labels.yearEndDate,
		endDateOfPeriodCovered: labels.endDateOfPeriodCovered
	};

	const handleOpenAllAccordions = () => setOpenAccordions(data.map(({name}) => name));
	const handleCloseAllAccordions = () => setOpenAccordions([]);

	const renderCustomizeColumns = (headerLabel) => {
		if (!columnVisibilityOptions) return false;

		const props = {
			options: columnVisibilityOptions,
			value: visibilityValue,
			onChange: (newValue) => setVisibilityValue(newValue),
			maxValues: maxCustomColumns,
			className: 'customize-columns',
			headerLabel
		};

		return <ColumnVisibilityDropdown {...props} />;
	};

	const renderSearch = () => {
		const props = {
			value: searchString,
			onEnter: () => {
				setSearch({searchString, filterString: searchString});
				handleOpenAllAccordions();
			},
			onChange: (newValue) => setSearch({searchString: newValue, filterString}),
			onClear: () => {
				setSearch({searchString: '', filterString: null});
				handleCloseAllAccordions();
			},
			placeholder: labels.searchEntitiesAndEngagements,
			className: 'search'
		};

		return <Search {...props} />;
	};

	const renderActualHeader = () => {
		return (
			<>
				<StyledMetricsTableHeader {...headerConfig}>
					{renderSearch()}
					{renderHeader && renderHeader(renderCustomizeColumns)}
				</StyledMetricsTableHeader>
				{renderFilter && renderFilter()}
			</>
		);
	};

	const renderEntity = ({id, data, name}) => {
		const renderGroupLabel = () => (
			<StyledGroupLabel>
				<OutputText body2 ellipsis>
					{name}
				</OutputText>
				<Badge variant="default" value={data.length} />
			</StyledGroupLabel>
		);

		const isOpen = openAccordions.includes(name);

		const accordionProps = {
			key: id,
			trigger: renderGroupLabel,
			open: isOpen,
			onClose: () => isOpen && setOpenAccordions(openAccordions.filter((n) => n !== name)),
			onOpen: () => !isOpen && setOpenAccordions([...openAccordions, name])
		};

		const props = {
			data,
			columns: columns(tableHeaderLabels, visibilityValue, userPreferences)[variant],
			pagination: true,
			headerMultiline: 2,
			headerHeightAuto: true
		};

		return <Accordion {...accordionProps}>{isOpen && <CustomTable {...props} />}</Accordion>;
	};

	const props = {
		list: displayData,
		item: renderEntity,
		enableScroll: false,
		emptySubtitle: labels.selectadditionalEntitiesToContinue
	};

	if (filterString) {
		props.emptyTitle = labels.noRecord;
		props.emptySubtitle = '';
		props.emptyIcon = IconoirSearch;
	}

	return (
		<StyledMetricsTable className="full">
			{renderActualHeader()}
			<ListWithScroll {...props} />
		</StyledMetricsTable>
	);
};

MetricsTable.propTypes = {
	renderHeader: PropTypes.func,
	headerConfig: PropTypes.shape({
		$areas: PropTypes.string,
		$columns: PropTypes.string
	}),
	renderFilter: PropTypes.func,
	variants: PropTypes.oneOf(variants),
	columnVisibilityOptions: columnVisibilityOptionsPropTypes,
	columnVisibility: PropTypes.arrayOf(PropTypes.string),
	maxCustomColumns: PropTypes.number
};

export default MetricsTable;
